import { ReactNode } from "react";
import { EyeOff, Gauge, History, Play, TriangleAlert, type LucideIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EvalKpiRow } from "./EvalKpiRow";
import { ChartSkeleton, TableSkeleton } from "./EvalSkeletons";
import { RunsTable } from "./RunsTable";
import { ScoreDistribution } from "./ScoreDistribution";
import { ScoreTrend } from "./ScoreTrend";
import { ShownCount } from "./primitives/notices";
import type { EvalKpis } from "./selectors/kpis";
import type { RunTableRow, ScoreBucket, TrendPoint } from "./selectors/runs";
import type { RunAgentLabels } from "./useEvalApi";

interface EvalRunsTabProps {
  kpis: EvalKpis;
  trend: TrendPoint[];
  distribution: ScoreBucket[];
  rows: RunTableRow[];
  agentLabels: RunAgentLabels;
  /** Runs on the customer before the status and agent facets, for the count. */
  totalRunCount: number;
  isLoading: boolean;
  /** Rows are on screen but their measured columns are not in yet. */
  resultsPending: boolean;
  /**
   * Runs in the table whose results the caller cannot read. Their measured
   * columns come back empty, which is indistinguishable from a run that judged
   * nothing, so the count is stated rather than left to be inferred.
   */
  unreadableRunCount: number;
  /** Runs whose results failed to load. Retryable, unlike the unreadable ones. */
  failedRunCount: number;
  /** Runs holding more results than the walk will read, so their figures are a sample. */
  truncatedRunCount: number;
  onLaunchRun: () => void;
  onRunDeleted: () => void;
}

/** Roughly the runs table's columns: agent, started, status, score, pass rate, duration. */
const RUN_COLUMN_WIDTHS = ["w-32", "w-24", "w-20", "w-16", "w-28", "w-16"];

/**
 * Run history: the headline numbers, the two charts, then the runs themselves. The
 * charts sit side by side because they answer a pair of questions only useful
 * together — how the suite is trending, and how the latest run is spread.
 */
export const EvalRunsTab = ({
  kpis,
  trend,
  distribution,
  rows,
  agentLabels,
  totalRunCount,
  isLoading,
  resultsPending,
  unreadableRunCount,
  failedRunCount,
  truncatedRunCount,
  onLaunchRun,
  onRunDeleted,
}: EvalRunsTabProps) => (
  <div className="flex flex-col gap-4">
    <EvalKpiRow kpis={kpis} isLoading={isLoading} />

    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Score by run</CardTitle>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Each run&apos;s mean case score. The band spans its best and worst case.
          </p>
        </CardHeader>
        <CardContent>{isLoading ? <ChartSkeleton /> : <ScoreTrend points={trend} />}</CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Latest run, by rubric level</CardTitle>
          <p className="mt-0.5 text-sm text-muted-foreground">
            How many cases the judges put at each level.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? <ChartSkeleton /> : <ScoreDistribution buckets={distribution} />}
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-4">
        <div className="flex items-baseline gap-2">
          <CardTitle className="text-base font-semibold">Runs</CardTitle>
          <ShownCount shown={rows.length} total={totalRunCount} noun="run" />
        </div>
        {/* Launching sits on the table it appends a row to, the way creating a
            case sits on the inventory. In the page header it read as the one
            thing this dashboard is for, when most visits here are to read a run
            somebody else's schedule already launched. */}
        <Button onClick={onLaunchRun}>
          <Play className="size-4" aria-hidden />
          Launch run
        </Button>
      </CardHeader>
      <CardContent>
        <MeasuredWindowNotice rows={rows} resultsPending={resultsPending} />
        {unreadableRunCount > 0 && (
          <RunNotice icon={EyeOff}>
            {unreadableRunCount} of these {unreadableRunCount === 1 ? "runs hides" : "runs hide"}{" "}
            results from you, so {unreadableRunCount === 1 ? "its" : "their"} score and pass rate
            read as unjudged. You need read access to every dataset and agent a run touched.
          </RunNotice>
        )}
        {failedRunCount > 0 && (
          <RunNotice icon={TriangleAlert}>
            {failedRunCount} {failedRunCount === 1 ? "run's results" : "runs' results"} failed to
            load, so {failedRunCount === 1 ? "its row reads" : "those rows read"} as unjudged.
            Reload to try again.
          </RunNotice>
        )}
        {truncatedRunCount > 0 && (
          <RunNotice icon={TriangleAlert}>
            {truncatedRunCount} {truncatedRunCount === 1 ? "run holds" : "runs hold"} more results
            than this page will load, so {truncatedRunCount === 1 ? "its" : "their"} score and pass
            rate are computed over a sample. Open the run for the whole of it.
          </RunNotice>
        )}
        {isLoading && rows.length === 0 ? (
          <TableSkeleton columns={RUN_COLUMN_WIDTHS} />
        ) : rows.length === 0 ? (
          <EmptyRuns onLaunchRun={onLaunchRun} />
        ) : (
          <RunsTable
            rows={rows}
            agentLabels={agentLabels}
            resultsLoading={resultsPending}
            onDeleted={onRunDeleted}
          />
        )}
      </CardContent>
    </Card>
  </div>
);

/**
 * Which runs the outcome columns cover, when the table lists more than it
 * measures. Silent when it measured everything it lists, and while the results
 * are still arriving — until they land nothing is measured, which is the
 * skeleton's business to say, not a count's.
 */
const MeasuredWindowNotice = ({
  rows,
  resultsPending,
}: {
  rows: RunTableRow[];
  resultsPending: boolean;
}) => {
  const measured = rows.filter((row) => row.summary != null).length;
  if (resultsPending || measured >= rows.length) {
    return null;
  }
  return (
    <RunNotice icon={Gauge}>
      Pass rate, mean score and cases cover the {measured} most recent runs — the same window as the
      figures above. Open an older run to see how it did.
    </RunNotice>
  );
};

/** One qualification of the table below it. Several can stack. */
const RunNotice = ({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) => (
  <p className="mb-3 flex items-start gap-1.5 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
    <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

/** No runs at all. A finding, not a wait: the placeholder covered that. */
const EmptyRuns = ({ onLaunchRun }: { onLaunchRun: () => void }) => (
  <div className="flex flex-col items-center gap-2 py-12 text-center">
    <History className="size-8 text-muted-foreground/60" aria-hidden />
    <p className="text-sm font-medium text-foreground">No eval runs match this filter</p>
    <p className="max-w-md text-sm text-muted-foreground">
      Runs arrive from the nightly schedule, from CI, or from a launch here.
    </p>
    <Button variant="outline" onClick={onLaunchRun} className="mt-2">
      <Play className="size-4" aria-hidden />
      Launch run
    </Button>
  </div>
);
